import { useEffect } from 'react';
import { ApiQueryPromise } from '@/types';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useSiteStore } from '@/store/site';
import { useGetSiteConfigs } from './use-site-configs';
import { useAccountStatus } from './account';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { PlayerSurveyDataInputs } from '@/lib/schemas/player-survey.schema';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { getAuthToken } from '@/store/auth';

interface SavePlayerSurveyResponse {
	ErrorCode: string;
	ErrorMsg: string;
	Success: boolean;
}

const checkIsSubmitted = async (): Promise<ApiQueryPromise<{ IsSubmitted: boolean }>> => {
	const { ctcdDomain } = useSiteStore.getState().apiDomain;
	const authToken = getAuthToken();

	const response = await fetchComposer.getWithBearer(
		cloudUrlParser(`${ctcdDomain}/api/CustomerSurvey/IsSubmitted/Galaxy`)
	)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const usePlayerSurveyIsSubmitted = () => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { data: actStatus } = useAccountStatus();

	const { data, refetch } = useQuery({
		queryKey: [queryKeys.PLAYER_SURVEY_IS_SUBMITTED],
		queryFn: checkIsSubmitted,
		refetchOnWindowFocus: false,
		cacheTime: 0,
		enabled: false
	});

	useEffect(() => {
		if (SiteConfigsData?.EnableGalaxyCustomerSurvey && actStatus === 2) {
			refetch(); // 有開活動開關&已存款才執行
		}
	}, [SiteConfigsData?.EnableGalaxyCustomerSurvey, actStatus, refetch]);

	return {
		isSubmitted: data?.data.Data?.IsSubmitted,
		refetch
	};
};

export const savePlayerSurvey = async (formData: PlayerSurveyDataInputs): Promise<ApiQueryPromise<SavePlayerSurveyResponse>> => {
	const convertRecommendOptions = (inputOptions: string) => inputOptions?.split(',').filter(Boolean).map(Number) ?? [];
	const formDataForApi = Object.assign(formData, { recommendOptions: convertRecommendOptions(formData.recommendOptions) });

	const { ctcdDomain } = useSiteStore.getState().apiDomain;
	const authToken = getAuthToken();

	const response = await fetchComposer.postWithBearer(
		cloudUrlParser(`${ctcdDomain}/api/CustomerSurvey/Galaxy`),
		{ body: JSON.stringify(formDataForApi) }
	)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	};

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
}